import type { ShopCurrency } from "./currency";

/** 6 ຄູ່ສຳລັບຟອມແກ້ອັດຕາ (ທຸກທິດທາງ LAK/THB/USD) — ລຳດັບຕາມ UX */
export const SHOP_FX_EDIT_PAIRS: ReadonlyArray<{
  from: ShopCurrency;
  to: ShopCurrency;
}> = [
  { from: "LAK", to: "THB" },
  { from: "THB", to: "LAK" },
  { from: "LAK", to: "USD" },
  { from: "USD", to: "LAK" },
  { from: "THB", to: "USD" },
  { from: "USD", to: "THB" },
];

/** ຄີຍ ສຳລັບ map ອັດຕາແບບ 1 base = rate quote */
export function exchangeRateKey(
  base: ShopCurrency,
  quote: ShopCurrency,
): string {
  return `${base}|${quote}`;
}

export function formatExchangeRateInputValue(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(n);
}

/** ສ້າງຄ່າເລີ່ມຕົ້ນຊ່ອງປ້ອນຈາກອັດຕາທີ່ມີຜົນ (ຕົງທິດ ຫຼື ອິນເວີດ) */
export function buildInitialFxRateInputsFromItems(
  items: ReadonlyArray<{
    baseCurrency: ShopCurrency;
    quoteCurrency: ShopCurrency;
    rate: number;
  }>,
): Record<string, string> {
  const map = new Map<string, number>();
  for (const r of items) {
    map.set(exchangeRateKey(r.baseCurrency, r.quoteCurrency), r.rate);
  }
  const out: Record<string, string> = {};
  for (const { from, to } of SHOP_FX_EDIT_PAIRS) {
    const k = exchangeRateKey(from, to);
    const direct = map.get(k);
    if (direct != null && direct > 0 && Number.isFinite(direct)) {
      out[k] = formatExchangeRateInputValue(direct);
      continue;
    }
    const inv = map.get(exchangeRateKey(to, from));
    if (inv != null && inv > 0 && Number.isFinite(inv)) {
      out[k] = formatExchangeRateInputValue(1 / inv);
    } else {
      out[k] = "";
    }
  }
  return out;
}

export type ConvertShopCurrencyResult = {
  convertedAmount: number;
  /** ຄູນຈາກຈຳນວນໃນ `from` ໄປເປັນ `to` */
  multiplier: number;
};

/**
 * ແປງຈຳນວນຈາກ `from` ໄປ `to` ໂດຍໃຊ້ຕາຕະລາງອັດຕາຕົງທິດ (1 base = rate quote) ຫຼືອິນເວີດ
 */
export function convertShopCurrencyAmount(
  amount: number,
  from: ShopCurrency,
  to: ShopCurrency,
  rates: ReadonlyMap<string, number>,
): ConvertShopCurrencyResult | null {
  if (from === to) {
    return { convertedAmount: amount, multiplier: 1 };
  }
  const direct = rates.get(exchangeRateKey(from, to));
  if (direct != null && direct > 0 && Number.isFinite(direct)) {
    return { convertedAmount: amount * direct, multiplier: direct };
  }
  const inv = rates.get(exchangeRateKey(to, from));
  if (inv != null && inv > 0 && Number.isFinite(inv)) {
    const multiplier = 1 / inv;
    return { convertedAmount: amount * multiplier, multiplier };
  }
  return null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** ຂັ້ນປ້ອນນ້ອຍສຸດຕາມສະກຸນ (ກັບຊ່ອງຊຳລະ POS) */
function paymentCurrencyInputStep(c: ShopCurrency): number {
  return c === "LAK" ? 1 : 0.01;
}

function floorToPaymentInputStep(
  q: number,
  paymentCurrency: ShopCurrency,
): number {
  const step = paymentCurrencyInputStep(paymentCurrency);
  if (step >= 1) return Math.floor(q + 1e-9);
  return Math.floor((q + 1e-9) * 100) / 100;
}

/**
 * ຈຳນວນໃນ `paymentCurrency` ທີ່ປ້ອນແລ້ວແປງກັບເປັນ `invoiceCurrency` ບໍ່ເກີນ `maxAmountInInvoice`
 * (ແກ້ການປັດສະແດງ + ທາງ direct/inverse ທີ່ເຮັດໃຫ້ປ້ອນຕາມ "≈" ແລ້ວເກີນຍອດ)
 */
export function suggestPaymentAmountNotOverInvoice(
  maxAmountInInvoice: number,
  invoiceCurrency: ShopCurrency,
  paymentCurrency: ShopCurrency,
  rates: ReadonlyMap<string, number>,
): number | null {
  if (!Number.isFinite(maxAmountInInvoice) || maxAmountInInvoice <= 0) {
    return null;
  }
  if (invoiceCurrency === paymentCurrency) {
    const q = floorToPaymentInputStep(maxAmountInInvoice, paymentCurrency);
    return q > 0 ? round2(q) : null;
  }
  const conv = convertShopCurrencyAmount(
    maxAmountInInvoice,
    invoiceCurrency,
    paymentCurrency,
    rates,
  );
  if (conv == null) return null;

  const step = paymentCurrencyInputStep(paymentCurrency);
  const eps = 1e-6;
  let q = floorToPaymentInputStep(conv.convertedAmount, paymentCurrency);

  const backToInvoice = (amt: number) =>
    convertShopCurrencyAmount(amt, paymentCurrency, invoiceCurrency, rates);

  for (let i = 0; i < 250_000; i++) {
    if (q < 0) return null;
    const back = backToInvoice(q);
    if (back == null) return null;
    if (back.convertedAmount <= maxAmountInInvoice + eps) {
      return q > 0 ? round2(q) : null;
    }
    q = round2(q - step);
  }
  return null;
}

export function buildDirectRateMapFromEffectiveRows(
  rows: ReadonlyArray<{
    baseCurrency: ShopCurrency;
    quoteCurrency: ShopCurrency;
    rate: number;
  }>,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    m.set(exchangeRateKey(r.baseCurrency, r.quoteCurrency), r.rate);
  }
  return m;
}
